var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Alert, Snackbar, Grid, Card, CardContent, } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, GetApp as ExportIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { jobService } from '@services/jobService';
import { formatDate } from '@utils/dateUtils';
import { businessService } from '@services/businessService';
import { useAuth } from '@contexts/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import JobModal from './JobModal';
const Jobs = ({ hideTitle = false }) => {
    const { isAdmin, user } = useAuth();
    const { machineId } = useParams();
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [businesses, setBusinesses] = useState([]);
    const [selectedBusinessId, setSelectedBusinessId] = useState('');
    const [loading, setLoading] = useState(true);
    const [selectedJob, setSelectedJob] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [jobToDelete, setJobToDelete] = useState(null);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [adminToastShown, setAdminToastShown] = useState(false);
    useEffect(() => {
        loadBusinesses();
    }, []);
    useEffect(() => {
        // If we're viewing from a specific machine, filter by that machine
        if (machineId) {
            const numMachineId = parseInt(machineId, 10);
            if (!isNaN(numMachineId)) {
                // We'll load all jobs but filter client-side for now
                // TODO: Update backend to support filtering by machineId once relationship is changed
            }
        }
    }, [machineId]);
    useEffect(() => {
        // Show admin notification toast on mount
        if (isAdmin && !adminToastShown) {
            setSnackbar({
                open: true,
                message: 'You are currently an admin user and can see all businesses, machines, service histories and jobs for all users',
                severity: 'info',
            });
            setAdminToastShown(true);
        }
    }, [isAdmin, adminToastShown]);
    useEffect(() => {
        if (businesses.length > 0) {
            loadJobs();
        }
    }, [selectedBusinessId, businesses]);
    const loadBusinesses = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const data = yield businessService.getAll();
            setBusinesses(data);
            // Set to user's default business if not viewing from a specific machine
            if (data.length > 0 && !machineId) {
                if (user === null || user === void 0 ? void 0 : user.defaultBusinessId) {
                    setSelectedBusinessId(user.defaultBusinessId);
                }
                else {
                    setSelectedBusinessId(data[0].id);
                }
            }
        }
        catch (error) {
            console.error('Error loading businesses:', error);
            showSnackbar('Error loading businesses', 'error');
        }
    });
    const loadJobs = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setLoading(true);
            // If viewing from a specific machine, use the machine-specific endpoint
            if (machineId) {
                const numMachineId = parseInt(machineId, 10);
                if (!isNaN(numMachineId)) {
                    const data = yield jobService.getByMachineId(numMachineId);
                    setJobs(data);
                }
            }
            else {
                // Otherwise, load all jobs for the selected business
                const data = selectedBusinessId
                    ? yield jobService.getAll(selectedBusinessId)
                    : yield jobService.getAll();
                setJobs(data);
            }
        }
        catch (error) {
            console.error('Error loading jobs:', error);
            showSnackbar('Error loading jobs', 'error');
        }
        finally {
            setLoading(false);
        }
    });
    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };
    const handleCloseSnackbar = () => {
        setSnackbar(Object.assign(Object.assign({}, snackbar), { open: false }));
    };
    const handleOpenModal = (job) => {
        setSelectedJob(job || null);
        setModalOpen(true);
    };
    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedJob(null);
    };
    const handleSaveJob = (jobData) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            if (selectedJob) {
                yield jobService.update(selectedJob.id, Object.assign(Object.assign({}, jobData), { id: selectedJob.id }));
                showSnackbar('Job updated successfully', 'success');
            }
            else {
                yield jobService.create(jobData);
                showSnackbar('Job created successfully', 'success');
            }
            handleCloseModal();
            loadJobs();
        }
        catch (error) {
            console.error('Error saving job:', error);
            showSnackbar('Error saving job', 'error');
        }
    });
    const handleOpenDeleteDialog = (job) => {
        setJobToDelete(job);
        setDeleteDialogOpen(true);
    };
    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setJobToDelete(null);
    };
    const handleDeleteJob = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!jobToDelete)
            return;
        try {
            yield jobService.delete(jobToDelete.id);
            showSnackbar('Job deleted successfully', 'success');
            handleCloseDeleteDialog();
            loadJobs();
        }
        catch (error) {
            console.error('Error deleting job:', error);
            showSnackbar('Error deleting job', 'error');
        }
    });
    const handleExportToExcel = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (jobs.length === 0) {
            showSnackbar('No jobs to export', 'error');
            return;
        }
        try {
            const businessName = ((_a = businesses.find(b => b.id === selectedBusinessId)) === null || _a === void 0 ? void 0 : _a.name) || 'All';
            const filename = `Jobs_${businessName}_${new Date().toISOString().split('T')[0]}.xlsx`;
            // Call API to get Excel file
            const blob = yield jobService.exportToExcel(selectedBusinessId);
            // Create download link
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
            showSnackbar('Jobs exported successfully', 'success');
        }
        catch (error) {
            console.error('Error exporting jobs:', error);
            showSnackbar('Error exporting jobs', 'error');
        }
    });
    return (_jsxs(Box, { children: [machineId && (_jsx(Button, { variant: "outlined", startIcon: _jsx(BackIcon, {}), onClick: () => navigate(`/machines/${machineId}`), sx: { mb: 2 }, children: "Back to Machine" })), !hideTitle && (_jsx(Typography, { variant: "h5", sx: { mb: 2 }, children: "Jobs" })), _jsx(Box, { sx: {
                    display: 'flex',
                    justifyContent: { xs: 'flex-end', md: 'space-between' },
                    alignItems: { xs: 'flex-start', md: 'center' },
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 3
                }, children: _jsxs(Box, { sx: {
                        display: 'flex',
                        gap: { xs: 1, md: 2 },
                        flexWrap: 'wrap',
                        width: { xs: '100%', md: 'auto' }
                    }, children: [_jsx(Button, { variant: "outlined", startIcon: _jsx(ExportIcon, {}), onClick: handleExportToExcel, disabled: jobs.length === 0, sx: {
                                flex: { xs: '1 1 auto', sm: '0 1 auto' },
                                fontSize: { xs: '0.8rem', md: '0.875rem' }
                            }, children: "Export" }), _jsx(Button, { variant: "contained", startIcon: _jsx(AddIcon, {}), onClick: () => handleOpenModal(), disabled: businesses.length === 0, sx: {
                                flex: { xs: '1 1 auto', sm: '0 1 auto' },
                                fontSize: { xs: '0.8rem', md: '0.875rem' }
                            }, children: "Add Job" })] }) }), loading ? (_jsx(Typography, { children: "Loading jobs..." })) : businesses.length === 0 ? (_jsx(Alert, { severity: "info", children: "No businesses found. Please create a business first before adding jobs." })) : (_jsx(Grid, { container: true, spacing: 2, children: jobs.map((job) => (_jsx(Grid, { item: true, xs: 12, children: _jsx(Card, { sx: {
                            cursor: 'pointer',
                            '&:hover': {
                                boxShadow: 6,
                                transform: 'translateY(-2px)',
                                transition: 'all 0.2s ease-in-out',
                            },
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                        }, onClick: () => handleOpenModal(job), children: _jsx(CardContent, { sx: { flexGrow: 1 }, children: _jsxs(Grid, { container: true, spacing: 2, alignItems: "center", children: [_jsxs(Grid, { item: true, xs: 12, sm: 8, children: [_jsx(Typography, { variant: "body2", color: "text.secondary", children: "Description" }), _jsx(Typography, { variant: "body1", children: job.description || 'No description' })] }), _jsxs(Grid, { item: true, xs: 12, sm: 2, children: [_jsx(Typography, { variant: "body2", color: "text.secondary", children: "Date Added" }), _jsx(Typography, { variant: "body1", fontWeight: "bold", children: formatDate(job.createdAt) })] }), _jsx(Grid, { item: true, xs: 12, sm: 2, children: _jsxs(Box, { sx: { display: 'flex', gap: 1, justifyContent: 'flex-end' }, children: [_jsx(Button, { size: "small", color: "primary", onClick: (e) => {
                                                        e.stopPropagation();
                                                        handleOpenModal(job);
                                                    }, sx: { minWidth: 'auto', px: 1 }, children: _jsx(EditIcon, { fontSize: "small" }) }), _jsx(Button, { size: "small", color: "error", onClick: (e) => {
                                                        e.stopPropagation();
                                                        handleOpenDeleteDialog(job);
                                                    }, sx: { minWidth: 'auto', px: 1 }, children: _jsx(DeleteIcon, { fontSize: "small" }) })] }) })] }) }) }) }, job.id))) })), _jsx(JobModal, { open: modalOpen, job: selectedJob, machineId: machineId ? parseInt(machineId, 10) : 0, onClose: handleCloseModal, onSave: handleSaveJob }), _jsxs(Dialog, { open: deleteDialogOpen, onClose: handleCloseDeleteDialog, children: [_jsx(DialogTitle, { children: "Confirm Delete" }), _jsx(DialogContent, { children: _jsxs(DialogContentText, { children: ["Are you sure you want to delete the job \"", jobToDelete === null || jobToDelete === void 0 ? void 0 : jobToDelete.name, "\"? This action cannot be undone."] }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: handleCloseDeleteDialog, children: "Cancel" }), _jsx(Button, { onClick: handleDeleteJob, color: "error", autoFocus: true, children: "Delete" })] })] }), _jsx(Snackbar, { open: snackbar.open, autoHideDuration: 6000, onClose: handleCloseSnackbar, anchorOrigin: { vertical: 'bottom', horizontal: 'center' }, children: _jsx(Alert, { onClose: handleCloseSnackbar, severity: snackbar.severity, sx: { width: '100%' }, children: snackbar.message }) })] }));
};
export default Jobs;
